import React, { useState } from 'react';
import { Button, Space, Input, Form, Select, Row, Col, Tag, Typography, notification } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { TableCustom } from '../../../components/TableCustom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { AppSelect } from '../../../constants/appSelect';
import { newObjectKeys } from '../../../utils/utility';
import { deleteVideoRequest, getListVideosRequest } from '../../../services/lichvietvideo/videoServiceRequest';
import isEqual from 'lodash/isEqual';

const { Text } = Typography;

const QuanLyVideo = () => {
  const navigate = useNavigate();
  const [formSearch] = Form.useForm();
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
  });

  const {
    data: dataVideos,
    isPending,
    refetch: refetchDataBlocks,
  } = useQuery({ queryKey: ['dataVideos', params], queryFn: () => getListVideosRequest(params) });

  const { mutate: mutateDeleteVideo } = useMutation({
    mutationFn: deleteVideoRequest,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({
          message: 'Xóa video thành công!',
        });
        refetchDataBlocks();
      } else {
        notification.error({
          message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
      });
    },
  });

  const handleSubmitForm = (values) => {
    const temp = { page: 1, limit: params.limit };
    if (values?.name) {
      Object.assign(temp, { name: values?.name });
    }
    if (values?.description) {
      Object.assign(temp, { description: values?.description });
    }
    if (values?.full_video_link) {
      Object.assign(temp, { full_video_link: values?.full_video_link });
    }
    if (values?.status >= 0) {
      Object.assign(temp, { status: [values?.status] });
    }
    if (!isEqual(temp, params)) {
      setParams(temp);
      return;
    }
    refetchDataBlocks();
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      minWidth: 70,
      fixed: 'left',
    },
    {
      title: 'Tên video',
      dataIndex: ['id', 'name'],
      key: 'id',
      render: (_, { name, id }) => (
        <Link to={`/lv-video/quan-ly-video/${id}/sua-video`} className="text-[#1677FF]">
          {name}
        </Link>
      ),
      minWidth: 150,
      width: 250,
      fixed: 'left',
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      render: (description) => <Text>{description}</Text>,
      minWidth: 250,
      width: 400,
    },
    {
      title: 'Link video',
      dataIndex: 'full_video_link',
      key: 'full_video_link',
      render: (full_video_link) => <Text>{full_video_link}</Text>,
      minWidth: 200,
      width: 400,
    },
    {
      title: 'Ảnh thumb',
      dataIndex: 'thumb',
      key: 'thumb',
      render: (thumb) => <Text>{thumb}</Text>,
      minWidth: 200,
      width: 400,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag className="px-2 py-1" color={AppSelect.STATUS?.find((item) => item?.value === status)?.color}>
          {AppSelect.STATUS?.find((item) => item?.value === status)?.label}
        </Tag>
      ),
      width: 120,
      fixed: 'right',
    },
    // {
    //   title: 'Cập nhật lần cuối',
    //   dataIndex: ['updated_by', 'updated_at'],
    //   key: 'updated_at',
    //   render: (_, { updated_by, updated_at }) => (
    //     <Space direction="vertical">
    //       {updated_by && <Text className="text-[#667085]">{updated_by}</Text>}
    //       {updated_at && <Text className="text-[#667085]">{updated_at}</Text>}
    //     </Space>
    //   ),
    // },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size={0}>
          <Button type="text" onClick={() => navigate(`/lv-video/quan-ly-video/${record?.id}/sua-video`)}>
            <EditOutlined />
          </Button>
          <Button type="text" onClick={() => mutateDeleteVideo({ id: record?.id })}>
            <DeleteOutlined />
          </Button>
        </Space>
      ),
      width: 90,
      fixed: 'right',
    },
  ];

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl">Danh sách video</h1>
        <Link to={'/lv-video/quan-ly-video/them-video'}>
          <Button type="primary">
            <PlusOutlined />
            Thêm video
          </Button>
        </Link>
      </div>

      <Form
        name="search_blocks"
        form={formSearch}
        layout="vertical"
        requiredMark={false}
        className="mb-5"
        onFinish={handleSubmitForm}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="name" label={<Text>Tên video</Text>}>
              <Input placeholder="Nhập tên video..." />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="description" label={<Text>Mô tả</Text>}>
              <Input placeholder="Nhập mô tả..." />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="full_video_link" label={<Text>Link video</Text>}>
              <Input placeholder="Nhập link video..." />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="status" label={<Text>Trạng thái</Text>} initialValue={-1}>
              <Select
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={[{ value: -1, label: 'Tất cả' }, ...AppSelect.STATUS]}
              />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2">
            <Button type="primary" htmlType="submit">
              <SearchOutlined />
              Tìm kiếm
            </Button>
            {params &&
              Object.values(newObjectKeys(params, ['name', 'description', 'full_video_link', 'status'])).some(
                (value) => value
              ) && (
                <Button
                  htmlType="button"
                  onClick={() => {
                    formSearch.resetFields();
                    setParams({ page: 1, limit: params.limit });
                  }}
                >
                  <DeleteOutlined />
                  Xóa lọc
                </Button>
              )}
          </Col>
        </Row>
      </Form>

      <TableCustom
        columns={columns}
        dataSource={dataVideos?.data}
        totalItem={dataVideos?.total}
        params={params}
        setParams={setParams}
      />
    </div>
  );
};

export default QuanLyVideo;
